import { type FC, useEffect, useState, useRef, type MouseEvent as ReactMouseEvent, type MutableRefObject } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../core';
import type { IconName } from '../../Icons/Icons';
import { FAIcon } from '../../Icons/Icons';
import Tooltip, { TooltipPreference } from '../../Tooltip/Tooltip';
import {
  type RestrictedFloatLabelInputFieldPropType,
  type LunarInputConfig,
  MD_MAX_LENGTH,
  XL_L_MAX_LENGTH,
  XS_SM_MAX_LENGTH,
} from '../types/propsType';
import { InputTypes, InputValidations } from '../types/enums';
import { TextLinkUnderlineStyle } from '../../TextLink/TextLink';
import RawTextLink from '../../TextLink/RawTextLink';
import { filterIconTheme } from '../../utils/ThemeFilter/ThemeFilter';
import { Theme } from '../../types/enums';
import { ViewPortSizes, useBreakpoint } from '../../Breakpoint/BreakpointProvider';
import { NextgenImage } from '../../NextgenImage/NextgenImage';
import styles from './FloatLabelInput.module.scss';
const cx = classNames.bind(styles);

const { VALID, INVALID } = InputValidations;

const currentIconAndStatusClass = (status: InputValidations | undefined) => {
  if (status === VALID) {
    return { currentIcon: { iconName: 'circle-check' as IconName }, statusClassName: 'input-validated-valid' };
  } else if (status === INVALID) {
    return { currentIcon: { iconName: 'square-exclamation' as IconName }, statusClassName: 'input-validated-invalid' };
  }
  return { currentIcon: null, statusClassName: '' };
};

const hasValidCaretState = (
  caretStart: number | null | undefined,
  caretEnd: number | null | undefined,
  prevLen: number | undefined,
  value: string | number | readonly string[] | undefined,
): boolean => typeof caretStart === 'number' && typeof caretEnd === 'number' && typeof prevLen === 'number' && !!value;

const getRangeSelectionDelta = (
  caretStart: number,
  caretEnd: number,
  prevLen: number,
  valLen: number,
  autoLeadingZero: boolean,
): number => {
  if (autoLeadingZero && prevLen === 1 && valLen === 2) {
    return 2;
  }
  return valLen - (prevLen - (caretEnd - caretStart));
};

const getSingleCursorDelta = (prevLen: number, valLen: number, autoLeadingZero: boolean): number => {
  if (prevLen > valLen) {
    return -1;
  }
  if (autoLeadingZero && prevLen === 1) {
    return 2;
  }
  return valLen - prevLen || 1;
};

const caretPositionSetter = (
  caretStart: number | null | undefined,
  caretEnd: number | null | undefined,
  prevLen: number | undefined,
  value: string | number | readonly string[] | undefined,
  containerRef: MutableRefObject<HTMLDivElement>,
  autoLeadingZero: boolean,
) => {
  if (!hasValidCaretState(caretStart, caretEnd, prevLen, value)) {
    return;
  }

  const start = caretStart as number;
  const end = caretEnd as number;
  const previousLength = prevLen as number;
  const valLen = String(value ?? '').length;
  const isRangeSelection = end !== start;

  const delta = isRangeSelection
    ? getRangeSelectionDelta(start, end, previousLength, valLen, autoLeadingZero)
    : getSingleCursorDelta(previousLength, valLen, autoLeadingZero);

  const position = start + delta;
  containerRef.current.querySelector('input')?.setSelectionRange(position, position);
};
const getLunarCls = (isLunarFormatted: boolean) => (isLunarFormatted ? styles['float-lunar-input'] : '');
/**
 * @description Input provide <FloatLabelInput></FloatLabelInput> with basic css;
 *  styling can be override through prop (className) only.
 *  by default : disabled, readOnly are false;
 *  Use unique name to distinguish input from from other input elements on same page.
 */

const FloatLabelInput: FC<RestrictedFloatLabelInputFieldPropType> = ({
  id = '',
  name = '',
  label = '',
  message = '',
  className = '',
  labelInfo = '',
  labelInfoPreference = TooltipPreference.TOP_CENTER,
  disabled = false,
  readOnly = false,
  required = false,
  isInputFocused,
  showAsteriskForMandatoryField = false,
  status,
  value,
  placeholder,
  inputIcon,
  prefix = '',
  ctaTextLink,
  iconImgSrc,
  iconClassName = '',
  inputIconOnClick,
  forwardedRef,
  isLunarFormatted = false,
  isSensitiveField = true,
  autoLeadingZero = false,
  ...restProps
}) => {
  const inputContainerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const caretPositionStart = useRef<number | null>();
  const caretPositionEnd = useRef<number | null>();
  const prevValueLen = useRef<number>();
  const inputType = restProps.type === InputTypes.NUMBER ? InputTypes.TEXT : restProps.type;
  const [isFocused, setIsFocused] = useState(!!(prefix || value));
  const [lunarInputConfig, setLunarInputConfig] = useState<LunarInputConfig>({
    width: '',
    ...{ ...optionalProp('lunarPlaceholder', placeholder) },
  });
  const [inputPrefix, setInputPrefix] = useState(prefix ?? '');
  const viewPortType = useBreakpoint();

  const { currentIcon, statusClassName } = currentIconAndStatusClass(status);

  const ignoreTheseSymbols = ['e', 'E', '+', '-'];
  const configs = ConfigService.getConfigs();
  const allowMasking = configs.NEXT_PUBLIC_ALLOW_MASKING_CHANGE;
  // To update component incase of prefix change
  useEffect(() => {
    if (!isFocused) {
      setIsFocused(!!(prefix || value));
    }
    // truncate prefix when length exceeds
    if (prefix) {
      let fieldMaxLength = 0;

      const { width: elementWidth } = inputContainerRef.current.getBoundingClientRect();
      const { MEDIUM, LARGE, XLARGE } = ViewPortSizes;
      if (elementWidth >= XLARGE || elementWidth >= LARGE) {
        fieldMaxLength = XL_L_MAX_LENGTH;
      } else if (elementWidth >= MEDIUM) {
        fieldMaxLength = MD_MAX_LENGTH;
      } else {
        fieldMaxLength = XS_SM_MAX_LENGTH;
      }
      if (fieldMaxLength) {
        const prefixAvaialbleLength = fieldMaxLength - String(value ?? '').length;
        setInputPrefix(
          prefixAvaialbleLength < prefix.length
            ? `...${prefix.substring(prefix.length - prefixAvaialbleLength, prefix.length)}`
            : prefix,
        );
      }
    }
    if (isLunarFormatted) {
      const currentValueLen = String(value ?? '').length;
      setLunarInputConfig({
        width: `${currentValueLen * 9 || 1}px`,
        ...{ ...optionalProp('lunarPlaceholder', placeholder?.substring(currentValueLen)) },
      });
    }

    caretPositionSetter(
      caretPositionStart.current,
      caretPositionEnd.current,
      prevValueLen.current,
      value,
      inputContainerRef,
      autoLeadingZero,
    );
  }, [prefix, value, viewPortType, isLunarFormatted]);
  // this id is being used in smartInput field for uncontrolled element operation.
  const inputID = `float-input-field-${id || name}`;

  const CInputClassname = cx(
    'input',
    inputIcon,
    statusClassName,
    inputIcon && 'input-icon-color',
    'input-default-behaviour',
  );
  const CInputLabelClassname = cx('float-input-label', { 'show-asterisk': required && showAsteriskForMandatoryField });
  const CInputContainer = cx('input-field-container', 'float-input-container', {
    'input-focused': value || isFocused,
    'input-highlight': isInputFocused,
    disabled: disabled,
    'read-only': readOnly,
  });
  return (
    <div id={id} className={CInputClassname} ref={inputContainerRef}>
      <div className={CInputContainer}>
        {label && (
          <label htmlFor={inputID} className={CInputLabelClassname} data-testid="input-label-text-test">
            {label}
          </label>
        )}
        <section className={`${styles['float-input-body']} ${getLunarCls(isLunarFormatted)}`}>
          {prefix && <div className={styles['float-partial-input']}>{inputPrefix}</div>}
          <input
            id={inputID}
            className={`${styles['float-input-field']} ${className}`}
            placeholder={label || prefix ? '' : placeholder}
            data-testid="float-input-test-id"
            name={name}
            ref={forwardedRef}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            value={value}
            onFocus={(e) => {
              setIsFocused(true);
              restProps?.onFocus?.(e);
            }}
            onBlur={(e) => {
              if (!isInputFocused) {
                setIsFocused(!!(prefix || value));
              }
              restProps?.onBlur?.(e);
            }}
            onKeyDown={(e) =>
              restProps?.type === InputTypes.NUMBER && ignoreTheseSymbols.includes(e.key) && e.preventDefault()
            }
            {...restProps}
            onSelect={() => {
              const inputEle = inputContainerRef.current.querySelector('input');
              caretPositionStart.current = inputEle?.selectionStart;
              caretPositionEnd.current = inputEle?.selectionEnd;
              const currentValLen = String(value ?? '').length;
              prevValueLen.current = autoLeadingZero && currentValLen === 0 ? 1 : currentValLen;
            }}
            style={{
              width: lunarInputConfig.width,
            }}
            type={inputType}
            data-dtrum-mask={allowMasking ? isSensitiveField : undefined}
          />
          {isLunarFormatted && (
            <label htmlFor={inputID} className={styles['input-lunar-placeholder']}>
              {lunarInputConfig.lunarPlaceholder}
            </label>
          )}
          {ctaTextLink?.text && (
            <RawTextLink
              underline={TextLinkUnderlineStyle.FIXED}
              theme={Theme.GRAY}
              {...ctaTextLink}
              {...optionalProp('disabled', disabled || ctaTextLink.disabled)}
              className={styles['input-cta-textlink']}
            />
          )}
        </section>
        {iconImgSrc ? (
          <div className={styles['image-container']}>
            <div className={`${styles['icon-image']} ${iconClassName}`}>
              <NextgenImage data-testid="input-box-img-test-id" src={iconImgSrc} alt="input-icon" />
            </div>
          </div>
        ) : (
          inputIcon && (
            <FAIcon
              {...inputIcon}
              {...optionalProp('theme', filterIconTheme(restProps.theme))}
              data-testid="input-box-icon-test-id"
              className={`${styles['input-icon']} ${styles['input-box-icon']} ${iconClassName}`}
              onClick={(e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => inputIconOnClick?.(e)}
            />
          )
        )}
        {labelInfo && labelInfo.trim() && (
          <Tooltip message={labelInfo} preference={labelInfoPreference} className={styles['icon-tool-tip']} />
        )}
      </div>
      {message && (
        <span
          role="presentation"
          aria-label="message"
          aria-details={`${label} input message`}
          className={`${styles['input-message']} ${styles['float-input-message']}`}
        >
          {currentIcon && (
            <FAIcon
              {...currentIcon}
              {...optionalProp('theme', filterIconTheme(restProps.theme))}
              data-testid="input-icon-test-id"
              className={styles['input-validation-message-icon']}
            />
          )}
          {message && <span className={styles['float-message']}>{message}</span>}
        </span>
      )}
    </div>
  );
};
FloatLabelInput.displayName = 'FloatLabelInput';
export default FloatLabelInput;
